import {
  GET_DEPARTMENTS,
  GET_IP_STATUSES,
  GET_SCHOOLS,
  GET_SUPPORT,
  GET_USER_ID_BY_COGNITO_ID,
  GET_USER_TYPES,
  IDEA_OWNERS,
  POST_INVITE_OTHERS,
  POST_REQUEST_INVITE,
  USERS,
  VERIFY_TOKEN,
} from '../constants/APIUrlConstants';
import { TENANT_ID } from '../constants/Constants';
import {
  DepartmentListResponse,
  FilterUsersResponse,
  UserDetailsRes,
  InternalServerError,
  InviteUserRequest,
  InviteUserResponse,
  RequestInviteParams,
  RequestInviteResponse,
  SchoolListResponse,
  PartnershipListResponse,
  UserProfileUpdate,
  UserProfileUpdatePayload,
  User,
  UserCountResponse,
  UserProfilesResponse,
  UserAccountStatus,
  UserRolesResponse,
  VerifyTokenResponse,
  IPStatusesListResponse,
} from '../types/types';
import axiosInstance from '../utils/Axios';
import { handleAxiosError } from './HandleError';

type GetUserTypesResponse = UserRolesResponse | InternalServerError | Error;

export const getUserTypes = async (): Promise<GetUserTypesResponse> => {
  try {
    const response = await axiosInstance.get<UserRolesResponse>(GET_USER_TYPES);
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('Failed to fetch user types:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetching user types',
    );
  }
};

type GetSupportResponse = PartnershipListResponse | InternalServerError | Error;

export const getSupport = async (): Promise<GetSupportResponse> => {
  try {
    const response =
      await axiosInstance.get<PartnershipListResponse>(GET_SUPPORT);
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error) {
    console.error('Failed to fetch Supports:', error);
    return handleAxiosError(error, 'an error occurred while fetching supports');
  }
};

type GetDepartmentsResponse =
  | DepartmentListResponse
  | InternalServerError
  | Error;

export const getDepartments = async (
  tenantId: number,
  isActive: boolean,
): Promise<GetDepartmentsResponse> => {
  try {
    const response = await axiosInstance.get<DepartmentListResponse>(
      GET_DEPARTMENTS,
      {
        params: {
          tenant_id: tenantId,
          is_active: isActive,
        },
      },
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error) {
    console.error('Failed to fetch departments:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetching departments',
    );
  }
};

type GetSchoolsResponse = SchoolListResponse | InternalServerError | Error;

export const getSchools = async (
  tenantId: number,
  isActive: boolean,
): Promise<GetSchoolsResponse> => {
  try {
    const response = await axiosInstance.get<SchoolListResponse>(GET_SCHOOLS, {
      params: {
        tenant_id: tenantId,
        is_active: isActive,
      },
    });
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch schools:', error);
    return handleAxiosError(error, 'an error occured while fetch schools');
  }
};

type GetIPStatusResponse = IPStatusesListResponse | InternalServerError | Error;

export const getIPStatus = async (): Promise<GetIPStatusResponse> => {
  try {
    const response =
      await axiosInstance.get<IPStatusesListResponse>(GET_IP_STATUSES);
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch ip status:', error);
    return handleAxiosError(error, 'an error occured while fetch ip statuses');
  }
};

type POSTInviteOthersResponse =
  | InviteUserResponse
  | InternalServerError
  | Error;

export const postInviteOthers = async (
  payload: InviteUserRequest,
): Promise<POSTInviteOthersResponse> => {
  try {
    const response = await axiosInstance.post<InviteUserResponse>(
      POST_INVITE_OTHERS,
      payload,
    );
    if (response.status === 201 && response.data) {
      return {
        status: response.status,
        ...response.data,
      };
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to post an invite:', error);
    return handleAxiosError(error, 'an error occurred while post an invite');
  }
};

type POSTRequestInviteResponse =
  | RequestInviteResponse
  | InternalServerError
  | Error;

export const postRequestInvite = async (
  payload: RequestInviteParams,
): Promise<POSTRequestInviteResponse> => {
  try {
    const response = await axiosInstance.post<RequestInviteResponse>(
      POST_REQUEST_INVITE,
      payload,
    );
    if (response.status === 201 && response.data) {
      return {
        status: response.status,
        ...response.data,
      };
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to post request invitation:', error);
    return handleAxiosError(
      error,
      'an error occurred while post request invitation',
    );
  }
};

type GetVerifyTokenResponse = VerifyTokenResponse | InternalServerError | Error;

export const VerifyToken = async (
  token: string,
): Promise<GetVerifyTokenResponse> => {
  try {
    const response = await axiosInstance.get<VerifyTokenResponse>(
      VERIFY_TOKEN + `?token=${token as string}`,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('Failed to verify the token:', error);
    return handleAxiosError(error, 'an error occurred while verify the token');
  }
};

export const UpdateUserStatus = async (
  userId: number,
  payload: UserAccountStatus,
): Promise<InternalServerError> => {
  try {
    const response = await axiosInstance.put<InternalServerError>(
      `${USERS}/${userId}/status`,
      payload,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to update the user status:', error);
    return handleAxiosError(
      error,
      'an error occurred while update the user status',
    );
  }
};

type GetUserIdByCognitoIdResponse = User | InternalServerError | Error;

export const getUserIdByCognitoId = async (
  cognitoId: string,
): Promise<GetUserIdByCognitoIdResponse> => {
  try {
    const response = await axiosInstance.get<User>(
      `${GET_USER_ID_BY_COGNITO_ID}/${cognitoId}`,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch user id:', error);
    return handleAxiosError(error, 'an error occured while fetch the user id');
  }
};

type GetUserInfoResponse = UserDetailsRes | InternalServerError | Error;

export const getUserDetails = async (
  userId: number,
): Promise<GetUserInfoResponse> => {
  try {
    const response = await axiosInstance.get<UserDetailsRes>(
      `${USERS}/${userId}`,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('Failed to fetch user details:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetching the user details.',
    );
  }
};

export const updateProfile = async (
  userProfilePayload: UserDetailsRes,
  userId: number,
): Promise<UserProfileUpdate | InternalServerError | Error> => {
  try {
    const payload: UserProfileUpdatePayload = {
      tenant_id: TENANT_ID,
      first_name: userProfilePayload.first_name,
      last_name: userProfilePayload.last_name,
      google_scholar_url: userProfilePayload.google_scholar_url,
      linkedin_url: userProfilePayload.linkedin_url,
      headline: userProfilePayload.headline,
      bio: userProfilePayload.bio,
      location: userProfilePayload.location,
      profile_image_url: userProfilePayload.profile_image_url,
      school_id: userProfilePayload.selected_school_id?.value,
      department_id: userProfilePayload.selected_department_id?.value,
    };

    const response = await axiosInstance.put<UserProfileUpdate>(
      USERS + '/' + userId,
      payload,
    );

    if (response.status === 200 && response.data) {
      return response.data;
    }
    return createErrorResponse(response.status, response.statusText);
  } catch (error: unknown) {
    console.error('Error updating profile:', error);
    return handleAxiosError(error, 'an error occured while updating profile');
  }
};

// Function to create a standardized error response
const createErrorResponse = (status: number, message: string) => ({
  status,
  code: 'INVALID_REQUEST',
  message,
});

type GetUsersResponse = UserProfilesResponse | InternalServerError | Error;

export const getUsers = async (
  currentPage: number,
  totalPages: number,
  userId: number,
  userTypeIds: number[],
  schoolId: number,
  departmentId: number,
  sortOrder: 'asc' | 'desc',
): Promise<GetUsersResponse> => {
  try {
    const params: URLSearchParams = new URLSearchParams({
      page: currentPage.toString(),
      page_size: totalPages.toString(),
    });

    // Add optional parameters conditionally
    if (userTypeIds && userTypeIds.length > 0) {
      params.append('user_type_id', userTypeIds.join(','));
    }
    if (schoolId) {
      params.append('school_id', schoolId.toString());
    }
    if (userId) {
      params.append('user_id', userId.toString());
    }
    if (sortOrder) {
      params.append('sort_order', sortOrder);
    }
    if (departmentId) {
      params.append('department_id', departmentId.toString());
    }
    const response = await axiosInstance.get<UserProfilesResponse>(
      `${USERS}?${params.toString()}`,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch users:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetching the users',
    );
  }
};

type GetUserCountResponse = UserCountResponse | InternalServerError | Error;

export const getUsersCount = async (
  users: { id: number; name: string }[],
  userTypeId: number,
  schoolId: number | null,
  departmentId: number | null,
): Promise<GetUserCountResponse> => {
  try {
    const userIds = users.map((user) => user.id);
    const params: string[] = [];

    if (userTypeId) {
      params.push(`user_type_id=${userTypeId}`);
    }

    if (userIds.length !== 0) {
      params.push(`user_id=${userIds.join(',')}`); // Joining user IDs with a comma
    }

    if (departmentId !== null) {
      // Check for null explicitly
      params.push(`department_id=${departmentId}`);
    }

    if (schoolId !== null) {
      // Check for null explicitly
      params.push(`school_id=${schoolId}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await axiosInstance.get<UserCountResponse>(
      `${USERS}/count${queryString}`,
    );

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch users count:', error);
    return handleAxiosError(error, 'an error occured while fetch users count');
  }
};

type GetFilterUsersResponse = FilterUsersResponse | InternalServerError | Error;

export const getFilterUsers = async (
  userTypeId?: number | null,
  schoolId?: number | null,
  departmentId?: number | null,
): Promise<GetFilterUsersResponse> => {
  try {
    const params: string[] = [];

    if (userTypeId) {
      params.push(`user_type_id=${userTypeId}`);
    }

    if (schoolId !== null && schoolId !== undefined) {
      params.push(`school_id=${schoolId}`);
    }

    if (departmentId !== null && departmentId !== undefined) {
      params.push(`department_id=${departmentId}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await axiosInstance.get<FilterUsersResponse>(
      `filter${USERS}${queryString}`,
    );

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('Failed to fetch the filter users:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetch the filter users',
    );
  }
};

type GetUsersHavingIdeasResponse =
  | FilterUsersResponse
  | InternalServerError
  | Error;

// NEED_TO_FIX the below api
export const getUsersHavingIdeas = async (
  schoolId?: number | null,
  departmentId?: number | null,
): Promise<GetUsersHavingIdeasResponse> => {
  try {
    const params: string[] = [];

    if (schoolId !== null) {
      params.push(`school_id=${schoolId}`);
    }

    if (departmentId !== null) {
      params.push(`department_id=${departmentId}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await axiosInstance.get<FilterUsersResponse>(
      `filter${IDEA_OWNERS}${queryString}`,
    );

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('Failed to fetch the filter users:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetch the filter users',
    );
  }
};
